const WORK_SIZE_MM = 160;
const u16 = (value) => [(value >>> 8) & 255, value & 255];
const u32 = (value) => [(value >>> 24) & 255, (value >>> 16) & 255, ...u16(value)];

export function buildFrame(func, data = []) {
  const checksum = (func + data.reduce((sum, byte) => sum + byte, 0)) & 65535;
  return Uint8Array.from([0xaa, 0xbb, data.length + 3, func, ...data, ...u16(checksum)]);
}

export const STOP_FRAME = buildFrame(255, [0, 0, 0, 0, 0]);
export const STATUS_FRAME = buildFrame(0, [0, 0, 0, 0, 0]);

export class LP4FrameParser {
  constructor() {
    this.buffer = [];
  }

  push(bytes) {
    this.buffer.push(...bytes);
    const frames = [];
    let pending = -1;
    for (let i = 0; i < this.buffer.length; i += 1) {
      if (this.buffer[i] !== 0xaa) continue;
      if (i + 1 === this.buffer.length) { if (pending < 0) pending = i; break; }
      if (this.buffer[i + 1] !== 0xbb) continue;
      const length = this.buffer[i + 2];
      if (length === undefined || i + 3 + length > this.buffer.length) {
        if (pending < 0) pending = i;
        continue;
      }
      if (length < 3) continue;
      const frame = this.buffer.slice(i, i + 3 + length);
      const sum = frame.slice(3, length + 1).reduce((total, byte) => total + byte, 0) & 65535;
      if (sum !== ((frame[length + 1] << 8) | frame[length + 2])) continue;
      frames.push(Uint8Array.from(frame));
      this.buffer.splice(0, i + 3 + length);
      i = -1;
      pending = -1;
    }
    this.buffer = pending < 0 ? [] : this.buffer.slice(pending);
    return frames;
  }
}

export function ldsSettings(preset = {}) {
  const percent = (value) => Number.isInteger(value) && value >= 1 && value <= 100;
  const mapped = percent(preset.power_pct) && percent(preset.depth_pct);
  return {
    power: mapped ? preset.power_pct : 46,
    depth: mapped ? preset.depth_pct : 84,
    times: Number.isInteger(preset.passes) && preset.passes >= 1 && preset.passes <= 255 ? preset.passes : 1,
    dpi: preset.dpi === undefined ? 254 : preset.dpi,
  };
}

export function buildStartFrame({ fileId, bounds, power, depth, times = 1 }) {
  if (![power, depth].every((value) => Number.isInteger(value) && value >= 1 && value <= 100)
    || !Number.isInteger(times) || times < 1 || times > 255) throw new Error("Invalid LP4 power, depth or repetitions");
  return buildFrame(1, [
    1, power, 101 - depth, ...u32(fileId),
    ...u16(Math.trunc(bounds.minX)), ...u16(Math.trunc(bounds.minY)),
    0, times, 0, ...u16(2000), 1, 0, 60, ...u16(0), 1,
  ]);
}

export function buildFillHeader({ width, height, fileId, segments, left, top, dpi, printName = "" }) {
  if (![254, 508].includes(dpi)) throw new Error("LP4 fill DPI must be 254 or 508");
  const header = new Uint8Array(64);
  header.set([0x40, ...u16(width), ...u16(height), ...u32(fileId), ...u32(segments),
    dpi === 254 ? 4 : 2, ...u16(left), ...u16(top), ...u16(dpi), 0]);
  header.set(Array.from(String(printName).replace(/[^\x20-\x7e]/g, "").slice(0, 30), (char) => char.charCodeAt(0)), 34);
  return header;
}

export function encodeFillRaster({ data, width, height }) {
  const records = [];
  let rowOrdinal = 0;
  for (let y = 0; y < height; y += 1) {
    const spans = [];
    let start = null;
    for (let x = 0; x < width; x += 1) {
      const gray = data[(y * width + x) * 4];
      if (start === null && gray <= 190) start = x;
      if (start !== null && (gray >= 245 || x === width - 1)) {
        spans.push([start, x]);
        start = null;
      }
    }
    if (!spans.length) continue;
    const reverse = rowOrdinal % 2 === 1;
    if (reverse) spans.reverse();
    for (const [start, end] of spans) {
      records.push(...u16((reverse ? end : start) + 1), ...u16(y + 1), ...u16(end - start));
    }
    rowOrdinal += 1;
  }
  return Uint8Array.from(records);
}

export function cropFillRaster({ data, width, height }) {
  let left = width, top = height, right = -1, bottom = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4] === 255) continue;
      left = Math.min(left, x); top = Math.min(top, y);
      right = Math.max(right, x); bottom = Math.max(bottom, y);
    }
  }
  if (right < 0) throw new Error("Text has no engravable pixels");
  if (right === width - 1 || bottom === height - 1) throw new Error("LP4 fill needs a white border inside the work area");
  const croppedWidth = right - left + 2, croppedHeight = bottom - top + 2;
  const cropped = new Uint8ClampedArray(croppedWidth * croppedHeight * 4);
  for (let y = 0; y < croppedHeight; y += 1) {
    const offset = ((top + y) * width + left) * 4;
    cropped.set(data.subarray(offset, offset + croppedWidth * 4), y * croppedWidth * 4);
  }
  return { data: cropped, width: croppedWidth, height: croppedHeight, left, top };
}

export function rasterizeContours(contours, dpi = 254) {
  if (![254, 508].includes(dpi)) throw new Error("LP4 fill DPI must be 254 or 508");
  const scale = dpi / 25.4;
  const canvas = document.createElement("canvas");
  canvas.width = WORK_SIZE_MM * scale;
  canvas.height = WORK_SIZE_MM * scale;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("Canvas rasterization is unavailable");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.scale(scale, scale);
  ctx.fillStyle = "#000";
  ctx.beginPath();
  for (const contour of contours) {
    if (!contour.length) continue;
    ctx.moveTo(contour[0].x, contour[0].y);
    for (const point of contour.slice(1)) ctx.lineTo(point.x, point.y);
    ctx.closePath();
  }
  ctx.fill("nonzero");
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

export function buildFillJob({ contours, preset, printName, fileId, rasterize = rasterizeContours }) {
  const settings = ldsSettings(preset);
  const points = contours?.flat();
  if (!points?.length || !contours.every((contour) => contour.length >= 3)) throw new Error("Text has no engravable outline");
  if (points.some(({ x, y }) => !Number.isFinite(x) || !Number.isFinite(y) || x < 0 || y < 0 || x > 160 || y > 160)) {
    throw new Error("Engraving bounds exceed the LP4 canvas work area");
  }
  const bounds = {
    minX: Math.min(...points.map(({ x }) => x)), minY: Math.min(...points.map(({ y }) => y)),
    maxX: Math.max(...points.map(({ x }) => x)), maxY: Math.max(...points.map(({ y }) => y)),
  };
  const crop = cropFillRaster(rasterize(contours, settings.dpi));
  const payload = encodeFillRaster(crop);
  if (!payload.length) throw new Error("Text has no engravable pixels");
  const id = fileId ?? crypto.getRandomValues(new Uint32Array(1))[0];
  const header = buildFillHeader({ ...crop, fileId: id, segments: payload.length / 6, dpi: settings.dpi, printName });
  return {
    fileId: id, header, payload, bounds, ...settings,
    beginFrame: buildFrame(5, [1, ...u32(64 + payload.length), 0]),
    startFrame: buildStartFrame({ fileId: id, bounds, ...settings }),
  };
}
